import moment from 'moment'

import { isEmpty, range, clone } from 'ramda'

import { MOMENT_FORMATS } from '@/localize'

import {
  VIEW_DAY as DAY,
  VIEW_WEEK as WEEK,
  VIEW_MONTH as MONTH,
  VIEW_AGENDA as AGENDA,
  VIEW_WAITLIST as WAITLIST,
  BOOKING_TYPE_RESERVATION,
  RESOURCE_TYPE_STAFFER,
} from '@/lib/consts.ts'

import {
  CALENDAR_HOUR_SLOT_HEIGHT,
  CALENDAR_MINUTE_SLOT_HEIGHT,
} from './consts'

export function getCalendarDates (date, view) {
  // shortcut: agenda or waitlist view
  if (view === AGENDA || view === WAITLIST) {
    const from = date.clone()
    return range(0, 7).map(i => from.clone().add(i, 'day'))
  }

  // shortcut: month view
  if (view === MONTH) {
    const from = date.clone().startOf('month').startOf('week')
    const till = date.clone().endOf('month').endOf('week')
    const dates = till.diff(from, 'days') + 1
    return range(0, dates).map(i => from.clone().add(i, 'day'))
  }

  // shortcut: week view
  if (view === WEEK) {
    const from = date.clone().startOf('week')
    return range(0, 7).map(i => from.clone().add(i, 'day'))
  }

  // shortcut: day view
  if (view === DAY) {
    return [date]
  }

  return []
}

export function sortEvents (a, b) {
  if (a.from.isAfter(b.from)) return 1
  else if (a.from.isBefore(b.from)) return -1
  else return 0
}

export function clockTop (from, gridHours) {
  const hour = from.hour() - gridHours.from
  return (hour * CALENDAR_HOUR_SLOT_HEIGHT) + Math.floor(from.minute() * CALENDAR_MINUTE_SLOT_HEIGHT)
}

/*
 *
 * calendar card shape
 *
 */
export function shapeFromEvent (event) {
  const adjustTill = (from, till) => moment.max(
    till,
    from.clone().add(15, 'minutes')
  )

  return event.hasGapHole
    ? [
      {
        from: event.from,
        till: adjustTill(event.from, event.gapStart),
      },
      {
        from: event.gapEnd,
        till: adjustTill(event.gapEnd, event.till),
      },
    ]
    : [
      {
        from: event.from,
        till: adjustTill(event.from, event.till),
      },
    ]
}

function calcMiddlePositionOfCard (offsetX, offsetY, cardHeight = 0) {
  const HEADER_OFFSET = 150 // top-bar + calendar's header
  const RESOURCES_HEADLINE_HEIGHT = 64
  return {
    x: offsetX - 50,
    y:
      (offsetY + cardHeight / 2)
      - (window.innerHeight / 2 - HEADER_OFFSET - RESOURCES_HEADLINE_HEIGHT),
  }
}

export function calculateCalendarCardOffset (id) {
  const card = document.getElementById(id)
  if (!card) return

  const centerPosition = calcMiddlePositionOfCard(
    (card.parentNode as HTMLElement).offsetLeft,
    card.offsetTop,
    card.clientHeight
  )
  return Object.assign(centerPosition, { card })
}

export function calcCardOffsetByData ({ resourceId, from }, gridHourFrom) {
  // get resource column node
  const resourceColumn = document.querySelector(`[data-resource="${resourceId}"]`)
  // get top offset calcuted by booked_from
  const cardTop = clockTop(moment(from), { from: gridHourFrom })
  const mockCardHeight = 0 // pixels
  return resourceColumn
    && !isEmpty(cardTop)
    && calcMiddlePositionOfCard((resourceColumn as HTMLElement).offsetLeft, cardTop, mockCardHeight)
}

function scrollToElement (element, { x, y, behavior }) {
  const fn = () => element.scrollTo({
    top: y,
    left: x,
    behavior,
  })
  if (behavior === 'smooth') {
    setTimeout(fn, 100)
  } else {
    fn()
  }
}

export function scrollCalendarToPoint (options) {
  const calendarContent = document.getElementById('calendar-content')
  if (calendarContent) {
    scrollToElement(calendarContent, options)
  }
}

export function addBounceToCard (card) {
  card.classList.add('bounce')
  setTimeout(() => {
    card.classList.remove('bounce')
  }, 1500)
}

function retrieveDateItems (date) {
  if (!date) return {}

  const dateAsMoment = moment(date)
  return {
    day: dateAsMoment.format(MOMENT_FORMATS.D),
    month: dateAsMoment.format('M'),
    dayOfWeek: dateAsMoment.format(MOMENT_FORMATS.ddd),
    shortMonthName: dateAsMoment.format('MMM'),
  }
}

export function createCombinedDate (startDate, endDate) {
  if (!startDate) return

  if (!endDate) {
    return moment(startDate).calendar(null, MOMENT_FORMATS.calendar)
  }

  const _startDate = retrieveDateItems(startDate)
  const _endDate = retrieveDateItems(endDate)

  const daysRange = `${_startDate.day}-${_endDate.day}`
  const monthsRange = _startDate.month === _endDate.month
    ? `${_startDate.shortMonthName}`
    : `${_startDate.shortMonthName}-${_endDate.shortMonthName}`
  const daysOfWeekRange = `${_startDate.dayOfWeek}-${_endDate.dayOfWeek}`

  return `${daysRange} ${monthsRange}, ${daysOfWeekRange}`
}

export function modelToResources (obj, booking, prop, _eventProxy, index) {
  if (!obj[booking[prop]]) {
    obj[booking[prop]] = {}
  }

  const id = booking.id ? booking.id : index

  obj[booking[prop]][id] = _eventProxy(clone(booking))
  return obj
}

export function makeCachedModelEvents ({
  type, subbookings, reservation, appointment_uid, resources,
}, modelEvents, _eventProxy) {
  try {
    // reservation
    if (type === BOOKING_TYPE_RESERVATION) {
      const resourceId = Object.keys(modelEvents.new)[0]
      const reservationEvent = {
        [reservation.id]: _eventProxy({
          ...modelEvents.new[resourceId]?.[0],
          id: reservation.id,
        }),
      }
      return {
        ...modelEvents,
        new: {
          [resourceId]: reservationEvent,
        },
        eventIds: [reservation.id],
      }
    }

    // booking
    const resourceProp = resources.type === RESOURCE_TYPE_STAFFER ? 'staffer_id' : 'appliance_id'
    return subbookings?.reduce((result, booking, index) => {
      if (booking[resourceProp] > -1) {
        if (booking.combo_children.length) {
          booking.combo_children.forEach(combo => {
            modelToResources(result.new, { ...combo, type, appointment_uid }, resourceProp, _eventProxy, index)
            result.eventIds.push(combo.id)
          })
        } else {
          modelToResources(result.new, { ...booking, type, appointment_uid }, resourceProp, _eventProxy, index)
          result.eventIds.push(booking.id)
        }
      }

      return result
    }, { resources: {}, events: {}, new: {}, eventIds: [] }) || modelEvents
  } catch (e) {
    return modelEvents
  }
}

